import { PATHS } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { useNavigate, useParams } from 'react-router'

import { cx } from '../atoms/cx.ts'
import { AddModal } from '../atoms/AddModal.tsx'
import { useBatchOwners } from '../screens/batches/useBatchOwners.tsx'
import { RecipeDetailContainer, RecipesContainer } from '../screens/recipes/RecipesContainer.tsx'
import {
  SampleDetailContainer,
  SampleNewContainer,
  SamplesContainer,
} from '../screens/samples/SamplesContainer.tsx'

/**
 * 「준비」 무리(시료 · 레시피)의 **목록 + 오른쪽 상세 판**(eric 2026-09-22 「준비의 시료, 레시피, 랩웨어도
 * 클릭하면 오른쪽에 정보가 보이게 — 방금 작업 한 거와 같이」). 작업 목록의 `BatchesSplitRoute` 와 같은 뼈대다.
 *
 * - 주소는 종전 그대로(`/samples` · `/samples/:sampleId` …)다. 새로고침 · 뒤로가기 · 모니터링의 시료 링크가 다 산다
 * - 목록은 늘 서고, `:id` 가 있으면 오른쪽 판이 열린다. 다른 행을 누르면 판의 내용만 바뀐다(`key` 로 판을 새로 세운다)
 * - 🔴 상세는 **같은 컨테이너**다. 판에 맞춰 두 벌을 만들지 않는다 — 장비 관점 콘솔은 그 컨테이너를 전체 화면으로 쓴다
 *
 * ⚠ 랩웨어 판은 2026-10-11 에 걷었다 — 서버가 옛 카탈로그(`/labware-definitions`)를 걷었고 대체는 레시피 › 템플릿의
 *   `labware` 종류다(정본 07 §4.8 · §5.9). 옛 `/labware…` 주소는 그리로 리다이렉트된다(`consoleResourceRoutes.tsx`).
 *
 * 라우트 표(`consoleResourceRoutes.tsx`)와 파일을 나눈 것은 한 파일이 컴포넌트만 내보내야 핫리로드가 성립해서다.
 */
export function SamplesSplitRoute() {
  const navigate = useNavigate()
  const { sampleId } = useParams()
  return (
    <div className={cx('splitpage', sampleId !== undefined && 'splitpage--open')}>
      <SamplesContainer resourceTab="samples" selectedSampleId={sampleId} />
      {sampleId === undefined ? null : (
        <aside
          aria-label={t('screen.sampleDetail.title')}
          className="splitpage__pane"
          key={sampleId}
        >
          <SampleDetailContainer onClose={() => void navigate(PATHS.samples)} />
        </aside>
      )}
    </div>
  )
}

/**
 * 시료 추가 — **시료 목록 위에 뜨는 창**(eric 2026-09-30 「준비에서 시료 추가 … 팝업창이 뜨도록」).
 * 레시피 · 랩웨어 추가(`consoleResourceModalViews.tsx`) · 새 작업(`NewBatchModalRoute`)과 같은 뼈대다.
 *
 * - 주소는 종전 그대로 `/samples/new` 라 **직접 링크 · 새로고침이 창을 되살린다**
 * - 닫으면(× · Esc · 바깥 누름 · 취소) 목록으로. 🔴 **보내는 중에는 안 닫힌다**(`DetailPage busy`)
 * - 다 등록되면 폼이 목록 주소로 간다 — 주소가 바뀌며 창이 닫힌다. 여러 줄을 한 번에 등록하는 화면이라
 *   「만든 것 하나의 서랍」이 없다(종전 페이지와 같다). 목록은 커맨드의 쿼리 무효화로 새로 받는다
 * - 폼은 **페이지 컴포넌트 그대로**다(`SampleNewScreen`). 창 안이면 `DetailPage` 가 제목 줄 · 「← 목록」을 접는다
 * - 덮개는 본문 범위(`scope="body"`) — 머리줄과 상시 작업 줄(E-STOP)은 창이 떠 있어도 눌린다(07 §6)
 * - ⚠ 페이지로 되돌리려면 라우트 표의 그 줄을 `SampleNewContainer` 로 바꾸면 끝이다(장비 관점 콘솔이 그렇게 쓴다)
 */
export function SampleNewModalRoute() {
  const navigate = useNavigate()
  return (
    <div className="splitpage">
      <SamplesContainer resourceTab="samples" />
      {/* 창은 추가 창 일곱이 `AddModal` 하나다 — 한 폭 · 한 열 */}
      <AddModal
        size="lg"
        onClose={() => void navigate(PATHS.samples)}
        title={t('screen.sampleNew.title')}
      >
        <SampleNewContainer />
      </AddModal>
    </div>
  )
}

export function RecipesSplitRoute() {
  const navigate = useNavigate()
  const { recipeId } = useParams()
  // 🔴 「작업자」 이름표 — admin 에게만 값이 있다(`GET /users` · 작업 목록과 같다). 없으면 열 · 줄이 안 선다
  const owners = useBatchOwners()
  return (
    /*
     * 🔴 **레시피도 1/3 이다**(eric 2026-09-30 「레시피도.. 해당 열을 클릭하면.. 1/3만 열리게 해줘」). 종전에는 편집 탭 줄 ·
     *    입력칸이 1/3 서랍을 넘쳐(1280 실측 449/343px) 여기만 종전 폭(약 1/2)이었다 — 서랍 안의 배치를 좁은 폭에 맞췄다
     *    (`RecipeDetailScreen` 의 탭 줄 · 표 열 우선순위).
     */
    <div className={cx('splitpage', recipeId !== undefined && 'splitpage--open')}>
      <RecipesContainer owners={owners} resourceTab="recipes" selectedRecipeId={recipeId} />
      {recipeId === undefined ? null : (
        <aside
          aria-label={t('screen.recipeDetail.title')}
          className="splitpage__pane"
          key={recipeId}
        >
          <RecipeDetailContainer onClose={() => void navigate(PATHS.recipes)} owners={owners} />
        </aside>
      )}
    </div>
  )
}
